<template>
  <Dialog
    is-persistent
    :title="dialogTitle"
    :dialog="isOpened"
    :confirmation-button-text="confirmationButtonText"
    @confirmationAction="saveProject"
  >
    <template #content>
      <v-form
        ref="form"
        v-model="valid"
      >
        <v-row class="pb-4">
          <v-col>
            <v-text-field
              id="project-title-input"
              v-model="title"
              :rules="titleValidationRules"
              data-cy="project-title-input"
              class="secondary-background"
              label="Title"
              hide-details
              required
              outlined
              dense
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pr-2">
            <DatePicker
              ref="startDate"
              v-model="startDate"
              label="Start date"
            />
          </v-col>
          <v-col>
            <DatePicker
              ref="endDate"
              v-model="endDate"
              label="End date"
              :min-date="startDate"
            />
          </v-col>
        </v-row>
        <v-row class="pt-4">
          <v-col>
            <v-select
              v-model="type"
              :menu-props="{ offsetY: true }"
              :items="projectTypes"
              class="secondary-background"
              label="Type"
              hide-details
              outlined
              dense
            />
          </v-col>
        </v-row>
        <v-row class="pt-4">
          <v-col>
            <v-select
              v-model="status"
              :menu-props="{ offsetY: true }"
              :items="projectStatusFilterValues"
              class="secondary-background"
              label="Status"
              hide-details
              outlined
              dense
            />
          </v-col>
        </v-row>
        <v-row class="pt-4">
          <v-col>
            <MultiChoiceFilter
              label="Office"
              class="secondary-background"
              item-text="name"
              required
              outlined
              dense
              hide-details
              :is-error="isOfficeError"
              :items="offices"
              :value="projectItem.officeIds"
              @update="officesSelected"
            />
          </v-col>
        </v-row>
        <v-row class="pt-4">
          <v-col>
            <v-select
              v-model="divisionId"
              :menu-props="{ offsetY: true }"
              :items="divisions"
              item-text="name"
              item-value="id"
              class="secondary-background"
              label="Division"
              hide-details
              outlined
              dense
              clearable
              required
              :rules="[v => !!v || 'Division is required']"
            />
          </v-col>
        </v-row>
      </v-form>
    </template>
  </Dialog>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import { mapCacheActions } from 'vuex-cache';

import { Dialog, DatePicker, MultiChoiceFilter } from '@/components/shared';
import { projectStatusFilterValues } from '@/constants/projectStatuses';
import { projectTypes } from '@/constants/projectTypes';
import { DateHelper } from '@/helpers/date.helper';
import { offices } from '@/store/shared/offices-module';

const getInitialStartDate = () => DateHelper.toIso(moment());
const getInitialEndDate = () => DateHelper.toIso(moment()
  .add(1, 'years'));

const defaultType = projectTypes.find((type) => type.isDefault) || projectTypes[0];
const defaultStatus = projectStatusFilterValues.find((status) => status.isDefault || projectStatusFilterValues[0]);

const getInitialState = () => ({
  date: {
    startDate: getInitialStartDate(),
    endDate: getInitialEndDate(),
  },
  title: '',
  type: defaultType.value,
  status: defaultStatus.value,
  officeIds: [],
  divisionId: null,
});

const generateUniqueProjectTitle = (baseTitle, existingTitles) => {
  const normalizedExisting = existingTitles.map((title) => title.toLowerCase().trim());
  const normalizedBase = baseTitle.toLowerCase().trim();

  if (!normalizedExisting.includes(normalizedBase)) {
    return baseTitle;
  }

  const copyTitle = `${baseTitle} (copy)`;

  if (!normalizedExisting.includes(copyTitle.toLowerCase().trim())) {
    return copyTitle;
  }

  let counter = 2;

  while (normalizedExisting.includes(`${baseTitle} (copy ${counter})`.toLowerCase().trim())) {
    counter += 1;
  }

  return `${baseTitle} (copy ${counter})`;
};

export default {
  components: {
    Dialog,
    DatePicker,
    MultiChoiceFilter,
  },
  props: {
    isOpened: {
      type: Boolean,
      required: true,
    },
    project: {
      type: Object,
      default: null,
    },
    templateProject: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      dialogTitle: 'Add Project',
      confirmationButtonText: 'Add',
      valid: true,
      projectItem: getInitialState(),
      title: '',
      startDate: getInitialStartDate(),
      endDate: getInitialEndDate(),
      type: defaultType.value,
      status: defaultStatus.value,
      divisionId: null,
      titleRules: [(value) => !!value || 'Title is required'],
      isOfficeError: false,
      projectStatusFilterValues,
      projectTypes,
    };
  },
  watch: {
    isOpened: {
      handler(value) {
        if (value && this.project) {
          this.loadProjectData();
        } else if (value && this.templateProject) {
          this.loadTemplateData();
        } else if (value) {
          this.reset();
        }
      },
      immediate: true,
    },
    startDate(value) {
      if (!DateHelper.isDayBefore(value, this.endDate)) {
        const newEndDate = DateHelper.toIso(moment(value)
          .endOf('month'));

        this.endDateChanged(newEndDate);
        this.endDate = newEndDate;
      }

      this.startDateChanged(value);
      this.startDate = value;
    },
    endDate(value) {
      this.endDateChanged(value);
    },
  },
  computed: {
    offices() {
      return offices;
    },
    ...mapGetters('projects/main', ['projectId']),
    ...mapGetters('projects/filters', ['allProjectsForOptions']),
    ...mapGetters('shared/offices', ['offices']),
    ...mapGetters('shared/divisions', ['divisions']),

    form() {
      return { ...this.projectItem };
    },
    isEditMode() {
      return !!this.project;
    },
    isTemplateMode() {
      return !!this.templateProject && !this.isEditMode;
    },
    existingProjectTitles() {
      return this.allProjectsForOptions.map((project) => project.title);
    },
    titleValidationRules() {
      const rules = [...this.titleRules];

      if (!this.isEditMode) {
        rules.push((value) => {
          if (!value) {
            return true;
          }

          const normalizedValue = value.toLowerCase().trim();
          const hasDuplicate = this.existingProjectTitles.some(
            (title) => title.toLowerCase().trim() === normalizedValue
          );

          return !hasDuplicate || 'Project with this title already exists';
        });
      }

      return rules;
    },
  },
  async created() {
    document.addEventListener('keydown', this.handleKeypress);
    document.addEventListener('keyup', this.handleKeyup);

    this.getOffices();
    this.getDivisions();
  },
  beforeUpdate() {
    if (this.isOpened && this.project) {
      this.dialogTitle = 'Edit Project';
      this.confirmationButtonText = 'Save';
    } else if (this.isOpened && !this.project) {
      this.dialogTitle = 'Add Project';
      this.confirmationButtonText = 'Add';
    }
  },
  destroyed() {
    document.removeEventListener('keydown', this.handleKeypress);
    document.removeEventListener('keyup', this.handleKeyup);
  },
  methods: {
    ...mapCacheActions('shared/offices', ['getOffices']),
    ...mapCacheActions('shared/divisions', ['getDivisions']),
    ...mapActions('projects/main', ['createProject', 'updateProject']),

    startDateChanged(value) {
      this.projectItem.date.startDate = value;
    },
    endDateChanged(value) {
      this.projectItem.date.endDate = value;
    },

    async saveProject(confirmed) {
      if (!confirmed) {
        this.closeDialog();

        return;
      }

      if (!this.validate()) {
        return;
      }

      const projectData = {
        title: this.title,
        type: this.type,
        status: this.status,
        start_date: this.projectItem.date.startDate,
        end_date: this.projectItem.date.endDate,
        office_ids: this.projectItem.officeIds,
        division_id: this.divisionId || null,
      };

      if (this.isEditMode) {
        projectData.id = this.project.id;
        try {
          await this.updateProject(projectData);
          this.$emit('save');
        } finally {
          this.closeDialog();
        }
      } else {
        if (this.isTemplateMode) {
          projectData.source_project_id = this.templateProject.id;
        }

        try {
          await this.createProject(projectData);
          this.$emit('save');
        } finally {
          if (this.projectId) {
            this.closeDialog();
            this.navigateToDetails(this.projectId);
          }
        }
      }
    },
    loadProjectData() {
      this.title = this.project.title || '';
      this.type = this.project.type || defaultType.value;
      this.status = this.project.status || defaultStatus.value;
      this.startDate = this.project.start_date || getInitialStartDate();
      this.endDate = this.project.end_date || getInitialEndDate();
      this.projectItem.officeIds = this.project.office_ids || [];
      this.divisionId = this.project.division_id || null;
      this.projectItem.date.startDate = this.startDate;
      this.projectItem.date.endDate = this.endDate;

      this.$nextTick(() => {
        if (this.$refs.startDate) {
          this.$refs.startDate.date = this.startDate;
        }
        if (this.$refs.endDate) {
          this.$refs.endDate.date = this.endDate;
        }
      });

      this.resetValidation();
    },
    loadTemplateData() {
      const template = this.templateProject;

      this.title = generateUniqueProjectTitle(template.title, this.existingProjectTitles);
      this.type = template.type || defaultType.value;
      this.status = template.status || defaultStatus.value;
      this.startDate = template.start_date || getInitialStartDate();
      this.endDate = template.end_date || getInitialEndDate();
      this.projectItem.officeIds = template.office_ids || [];
      this.divisionId = template.division_id || null;
      this.projectItem.date.startDate = this.startDate;
      this.projectItem.date.endDate = this.endDate;

      this.$nextTick(() => {
        if (this.$refs.startDate) {
          this.$refs.startDate.date = this.startDate;
        }
        if (this.$refs.endDate) {
          this.$refs.endDate.date = this.endDate;
        }
      });

      this.resetValidation();
    },
    closeDialog() {
      this.$emit('close');
      this.reset();
    },
    reset() {
      Object.assign(this.projectItem, getInitialState());
      this.title = '';
      this.type = defaultType.value;
      this.status = defaultStatus.value;
      this.startDate = getInitialStartDate();
      this.endDate = getInitialEndDate();
      this.divisionId = null;

      if (this.$refs.startDate) {
        this.$refs.startDate.date = getInitialStartDate();
      }
      if (this.$refs.endDate) {
        this.$refs.endDate.date = getInitialEndDate();
      }

      this.resetValidation();
    },
    validate() {
      if (!this.projectItem.officeIds.length) {
        this.isOfficeError = true;
      }

      return this.$refs.form.validate() && !this.isOfficeError;
    },
    handleKeypress(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDialog();
      }
    },
    handleKeyup(event) {
      if (event.key === 'Enter' && event.ctrlKey) {
        event.preventDefault();
        this.saveProject();
      }
    },
    officesSelected(value) {
      this.projectItem.officeIds = value;
      this.isOfficeError = !value.length;
    },
    resetValidation() {
      this.$refs.form.resetValidation();
      this.isOfficeError = false;
    },
    navigateToDetails(projectId) {
      this.$router.push({
        name: 'Project Details',
        params: { id: projectId },
      });
    },
  },
};
</script>

